import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button, Flex, Modal } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    cancelRegistrationRequest,
    registerContestRequest,
    RootState,
    selectMobileDeviceState,
    selectUserLoggedIn,
    selectUserInfo,
} from '../../modules';
import { Contest } from '../../modules/public/contest';
const { confirm } = Modal;

const ContestCard: React.FC<{ contest: Contest; numberOfParticipants: number; loading?: boolean }> = ({
    contest,
    numberOfParticipants,
}) => {
    const dispatch = useDispatch();
    const [deadline, setDeadline] = useState(0);
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const currentUser = useSelector((state: RootState) => selectUserInfo(state));
    const [messageContest, setMessageContest] = useState('');
    const [messageDate, setMessageDate] = useState('');
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const [isStarted, setIsStarted] = useState(new Date(contest?.startDateTime ?? 0) <= new Date());
    const [isEnded, setIsEnded] = useState(new Date(contest?.endDateTime ?? 0) <= new Date());
    const isLogin = useSelector((state: RootState) => selectUserLoggedIn(state));
    const isMobile = useSelector(selectMobileDeviceState);
    
    // Check if current user is registered by comparing uid with participants list
    const isRegistered = React.useMemo(() => {
        if (!currentUser?.uid || !contest?.participants) return false;
        return contest.participants.some((participant: any) => participant.uid === currentUser.uid);
    }, [currentUser?.uid, contest?.participants]);

    const setValidDeadline = () => {
        const now = moment().valueOf();
        const endTime = moment(contest.endDateTime).valueOf();
        const startTime = moment(contest.startDateTime).valueOf();

        if (endTime < now && startTime < now) {
            setDeadline(0);
        } else if (endTime > now && startTime <= now) {
            setDeadline(endTime);
        } else {
            setDeadline(startTime);
        }
    };
    const setStatus = () => {
        const now = moment().valueOf();
        const endTime = moment(contest.endDateTime).valueOf();
        const startTime = moment(contest.startDateTime).valueOf();

        if (endTime < now && startTime < now) {
            setIsEnded(true);
            setIsStarted(true);
        } else if (endTime > now && startTime <= now) {
            setIsEnded(false);
            setIsStarted(true);
        } else {
            setIsEnded(false);
            setIsStarted(false);
        }
    };

    function getTimeLeft() {
        if (deadline === 0) {
            return {
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
            };
        }
        const now = moment().valueOf();
        const diff = Math.max(deadline - now, 0);

        return {
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((diff / (1000 * 60)) % 60),
            seconds: Math.floor((diff / 1000) % 60),
        };
    }
    function getMessage() {
        if (isEnded) {
            return 'ENDED';
        } else if (isStarted) {
            return 'WILL END';
        } else {
            return 'WILL START';
        }
    }
    function getMessageDate() {
        if (isEnded) {
            return moment(contest.endDateTime).format('LLLL');
        } else if (isStarted) {
            return moment(contest.endDateTime).format('LLLL');
        } else {
            return moment(contest.startDateTime).format('LLLL');
        }
    }
    useEffect(() => {
        try {
            setValidDeadline();
            setStatus();
            setMessageContest(getMessage());
            setMessageDate(getMessageDate());
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            intervalRef.current = setInterval(() => {
                const now = new Date();
                const endTime = new Date(contest.endDateTime);
                const extendedEndTime = new Date(endTime.getTime() + 10 * 1000); // Thêm 10 giây

                if (endTime && extendedEndTime > now) {
                    setTimeLeft(getTimeLeft());
                    setMessageContest(getMessage());
                    setMessageDate(getMessageDate());
                } else {
                    clearInterval(intervalRef.current!);
                }
            }, 1000);

            return () => {
                if (intervalRef.current) clearInterval(intervalRef.current);
            };
        } catch (e) {
            return;
        }
    }, [deadline, timeLeft]);

    const handleRegisterContest = useCallback(() => dispatch(registerContestRequest(contest)), [dispatch, contest]);
    const handleCancelRegistration = useCallback(
        () => dispatch(cancelRegistrationRequest(contest)),
        [dispatch, contest],
    );

    const showLoginModal = () => {
        Modal.info({
            title: <p style={{ color: 'var(--primary-text-color)' }}>Login required</p>,
            centered: true,
            content: (
                <div style={{ color: 'var(--primary-text-color)' }}>
                    <p>
                        Please <a href="/signin">login</a> to join the contest.
                    </p>
                </div>
            ),
            onOk() {},
        });
    };

    const showPromiseConfirm = (msg: string, handleModal: () => void) => {
        confirm({
            title: <p style={{ color: 'var(--primary-text-color)' }}>{`Do you want to ${msg}?`}</p>,
            icon: <ExclamationCircleFilled />,
            centered: true,
            content: (
                <p
                    style={{
                        color: 'var(--primary-text-color)',
                    }}>{`When clicked the OK button, you will be ${msg} after 1 second.`}</p>
            ),
            onOk() {
                return new Promise<void>((resolve, reject) => {
                    handleModal();
                    setTimeout(() => (Math.random() > 0.5 ? resolve() : reject()), 1000);
                }).catch(() => console.log('Oops errors!'));
            },
            onCancel() {},
        });
    };

    const renderButton = () => {
        if (!contest) return null; // Tránh lỗi khi contest chưa có dữ liệu
        
        // If contest is strict (restricted), don't show join/cancel buttons
        if (contest.isStrict) {
            return (
                <Button
                    className="status-btn"
                    style={{ color: 'black', cursor: 'not-allowed', backgroundColor: '#f5f5f5' }}>
                    INVITE ONLY
                </Button>
            );
        }
        
        // If contest has ended
        if (isEnded) {
            return (
                <Button
                    className="status-btn"
                    style={{ color: 'black', cursor: 'not-allowed', backgroundColor: '#f5f5f5' }}>
                    CONTEST ENDED
                </Button>
            );
        }
        
        // If contest has started (but not ended)
        if (isStarted) {
            if (isRegistered) {
                return (
                    <Button
                        className="status-btn"
                        style={{ color: 'black', cursor: 'not-allowed', backgroundColor: '#f5f5f5' }}>
                        JOINED
                    </Button>
                );
            }
            // User not registered and contest already started - can't join anymore
            return (
                <Button
                    className="status-btn"
                    style={{ color: 'black', cursor: 'not-allowed', backgroundColor: '#f5f5f5' }}>
                    CONTEST STARTED
                </Button>
            );
        }
        
        // Contest hasn't started yet
        if (numberOfParticipants >= contest.maxParticipants && contest.maxParticipants > 0) {
            if (isRegistered) {
                return (
                    <Button
                        className="status-btn"
                        onClick={() =>
                            showPromiseConfirm('cancel registration of this contest', handleCancelRegistration)
                        }>
                        UNREGISTER
                    </Button>
                );
            }
            return (
                <Button
                    className="status-btn"
                    style={{ color: 'black', cursor: 'not-allowed', backgroundColor: '#f5f5f5' }}>
                    FULL
                </Button>
            );
        }
        
        if (isLogin) {
            return isRegistered ? (
                <Button
                    className="status-btn"
                    onClick={() =>
                        showPromiseConfirm('cancel registration of this contest', handleCancelRegistration)
                    }>
                    UNREGISTER
                </Button>
            ) : (
                <Button
                    className="status-btn"
                    onClick={() => showPromiseConfirm('register this contest', handleRegisterContest)}>
                    REGISTER
                </Button>
            );
        }

        return (
            <Button
                className="status-btn join-button"
                title="Please login to join the contest"
                onClick={showLoginModal}>
                JOIN THE CONTEST
            </Button>
        );
    };

    return (
        <Flex vertical gap="1rem" justify="center" align="space-between" className="countdown-container">
            <div style={{ position: 'relative', height: '400px', marginBottom: '2rem' }}>
                <img src={contest.banner} alt="banner" className="background-image" />
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -60%)',
                        width: '100%',
                        textAlign: 'center',
                        zIndex: 3,
                        pointerEvents: 'none',
                    }}>
                    {/* <Title level={1} className="contest-title" style={{
                        fontSize: '3rem',
                        fontWeight: 900,
                        letterSpacing: 1,
                        color: '#fff',
                        textShadow: '0 4px 24px rgba(0,0,0,0.7), 0 1px 0 #222',
                        margin: 0,
                        padding: 0,
                        lineHeight: 1.1,
                        background: 'rgba(0,0,0,0.15)',
                        borderRadius: 8,
                        display: 'inline-block',
                        maxWidth: '90%',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                    }}>
                        {contest.contestName.toUpperCase()}
                    </Title> */}
                </div>
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: '2.5rem',
                        transform: 'translateX(-50%)',
                        zIndex: 2,
                    }}>
                    {/* {isLogin ? (
                        <div className="participants-section" style={{ position: 'relative', marginBottom: '0.5rem' }}>
                            <div
                                className="participants-label"
                                style={{
                                    position: 'absolute',
                                    top: '-30px',
                                    left: '50%',
                                    transform: 'translateX(-50%) rotate(-10deg)',
                                    background: 'rgba(0,0,0,0.7)',
                                    color: '#fff',
                                    padding: '2px 10px',
                                    borderRadius: '8px',
                                    fontSize: '0.9rem',
                                    pointerEvents: 'none',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                People signed up for the competition
                            </div>
                            <div className="participants-avatars" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 40 }}>
                                {[...Array(Math.min(numberOfParticipants, 6))].map((_, idx) => (
                                    <img
                                        key={idx}
                                        src="https://static.vecteezy.com/system/resources/previews/009/292/244/non_2x/default-avatar-icon-of-social-media-user-vector.jpg"
                                        alt="participant"
                                        style={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            border: '2px solid #fff',
                                            marginLeft: idx === 0 ? 0 : -15,
                                            zIndex: 10 - idx,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                            background: '#fff',
                                        }}
                                    />
                                ))}
                                {numberOfParticipants > 6 && (
                                    <div
                                        style={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            background: '#222',
                                            color: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '1rem',
                                            marginLeft: -15,
                                            zIndex: 4,
                                            border: '2px solid #fff',
                                        }}
                                    >
                                        +{numberOfParticipants - 6}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="login-prompt-section" style={{ position: 'relative', marginBottom: '0.5rem' }}>
                            <div
                                className="login-prompt-content"
                                onClick={showLoginModal}
                                title="Please login to view contest participants">
                                <span>
                                    Sign in to view contest participants
                                </span>
                            </div>
                        </div>
                    )} */}
                </div>
                <div style={{ position: 'absolute', right: isMobile ? 0 : 32, bottom: isMobile ? 0 : 32, zIndex: 3 }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: 'rgba(255,255,255,0.92)',
                            color: '#222',
                            borderRadius: 16,
                            padding: '0.75rem 1.5rem',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
                            fontWeight: 700,
                            fontSize: '1.15rem',
                            gap: 10,
                        }}>
                        <svg width="24" height="24" fill="none" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                            <path
                                d="M7 2v2M17 2v2M3 7h18M5 11v6a2 2 0 002 2h10a2 2 0 002-2v-6M9 15h6"
                                stroke="#222"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <span>Ends: {moment(contest.endDateTime).format('dddd, MMMM D, YYYY [at] h:mm A')}</span>
                    </div>
                </div>
            </div>

            <Flex
                justify="center"
                align="center"
                vertical={isMobile ? true : false}
                gap="2rem"
                className="countdown-section">
                <div className="countdown-timer" style={{ gap: isMobile ? '0.25rem' : '2rem' }}>
                    <div className="timer-item">
                        <div className="timer-number">{timeLeft.days.toString().padStart(2, '0')}</div>
                        <div className="timer-label">DAYS</div>
                    </div>
                    <div className="timer-item">
                        <div className="timer-number">{timeLeft.hours.toString().padStart(2, '0')}</div>
                        <div className="timer-label">HOURS</div>
                    </div>
                    <div className="timer-item">
                        <div className="timer-number">{timeLeft.minutes.toString().padStart(2, '0')}</div>
                        <div className="timer-label">MINUTES</div>
                    </div>
                    <div className="timer-item">
                        <div className="timer-number">{timeLeft.seconds.toString().padStart(2, '0')}</div>
                        <div className="timer-label">SECONDS</div>
                    </div>
                </div>
                {renderButton()}
            </Flex>
        </Flex>
    );
};

export default ContestCard;
